import React, { useRef, useState, useEffect, useCallback } from 'react'
import { sortSpecializationChips } from '../../../domain/center'
import Tag from '@/components/atoms/Tag/Tag'
import { removeSpecializationMatchingPrimaryFocus } from '@/domain/center/useCases/removeSpecializationMatchingPrimaryFocus'
import { filterOutSpecializationsByCooccuringDisorders } from '@/domain/center/useCases/filterOutSpecializationsByCooccuringDisorders'
import { filterOutSpecializationsByPrimaryFocusMap } from '@/domain/center/useCases/filterOutSpecializationsByPrimaryFocusMap'
import { isNonEmptyString } from '@/utils/checks'
import { FeedMode } from '@/types/models/Term.model'
import { Department } from '@/domain/department/department'
import { getPrimaryFocusLabelByDepartmentAndFeedMode } from '@/domain/center/useCases/getPrimaryFocusLabelByDepartmentAndFeedMode'
import { getCoOccurringDisorderLabelByDepartmentAndFeedMode } from '@/domain/center/useCases/GetCoOccurringDisorderLabelByDepartmentAndFeedMode'
import { createPrimaryFocusMap } from '@/domain/term/useCases/createPrimaryFocusMap'
import { PrimaryFocus } from '@/domain/primaryFocus/constant'

interface SpecializationChipsProps {
  new_specializations: string[]
  highlighted_specializations: string[]
  primaryFocus?: string
  coOccurringDisorder?: string
  termFeedMode?: FeedMode
  department?: Department
}

const SpecializationChips: React.FC<SpecializationChipsProps> = ({
  new_specializations,
  highlighted_specializations,
  primaryFocus = '',
  coOccurringDisorder = '',
  termFeedMode = FeedMode.NONE,
  department = Department.Default
}) => {
  const [shouldShowMore, setShouldShowMore] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const primaryFocusMap = createPrimaryFocusMap()

  const displayPrimaryFocus = getPrimaryFocusLabelByDepartmentAndFeedMode({
    primaryFocus,
    termFeedMode,
    department
  })

  const displayCoOccurringDisorder =
    getCoOccurringDisorderLabelByDepartmentAndFeedMode({
      coOccurringDisorder,
      termFeedMode,
      department
    })

  const sortedSpecializations = sortSpecializationChips(
    new_specializations,
    highlighted_specializations
  )

  const filteredSpecializationsByPrimaryFocus =
    removeSpecializationMatchingPrimaryFocus({
      specializations: sortedSpecializations,
      primaryFocus
    }) || []

  const filteredSpecializations =
    department === Department.MentalHealth
      ? filterOutSpecializationsByPrimaryFocusMap({
          specializations: filteredSpecializationsByPrimaryFocus,
          primaryFocusMap,
          termFeedMode,
          primaryFocusToFilter: PrimaryFocus.SUBSTANCE_USE
        })
      : filteredSpecializationsByPrimaryFocus

  const filteredSpecializationsByCooccurringDisorders =
    filterOutSpecializationsByCooccuringDisorders(
      filteredSpecializations,
      coOccurringDisorder
    ) || []

  const checkVisibility = useCallback(() => {
    const container = containerRef.current
    if (!container) return

    const containerRect = container.getBoundingClientRect()
    const tags = container.querySelectorAll('.specialization-tag')

    let count = 0
    tags.forEach((tag) => {
      const tagRect = tag.getBoundingClientRect()
      if (
        tagRect.top >= containerRect.top &&
        tagRect.bottom <= containerRect.bottom
      ) {
        count++
      }
    })
    setShouldShowMore(
      count < filteredSpecializationsByCooccurringDisorders.length
    )
  }, [filteredSpecializationsByCooccurringDisorders.length])

  useEffect(() => {
    const handleResize = () => {
      setTimeout(checkVisibility, 50) // Delay to allow layout changes
    }

    checkVisibility()
    window.addEventListener('resize', handleResize)

    return () => window.removeEventListener('resize', handleResize)
  }, [checkVisibility])

  return (
    <div
      ref={containerRef}
      className="flex max-h-[52px] flex-wrap gap-x-1.5 gap-y-2 overflow-hidden md:max-h-[22px]"
    >
      {isNonEmptyString(displayPrimaryFocus) && (
        <Tag
          backgroundColor="bg-blue-50"
          className="!py-.5 !px-1.5"
          key={displayPrimaryFocus}
        >
          {displayPrimaryFocus}
        </Tag>
      )}

      {isNonEmptyString(displayCoOccurringDisorder) && (
        <Tag
          className="!py-.5 !px-1.5"
          key={displayCoOccurringDisorder}
        >
          {displayCoOccurringDisorder}
        </Tag>
      )}

      {filteredSpecializationsByCooccurringDisorders.map((item) => (
        <Tag
          className="specialization-tag !py-.5 !px-1.5"
          key={item}
        >
          {item}
        </Tag>
      ))}

      {shouldShowMore && (
        <span className="inline font-semibold text-gray-900 underline md:no-underline md:hover:underline">
          More
        </span>
      )}
    </div>
  )
}

export default SpecializationChips
